<template>
  <div>
    <div
      v-if="canShowPage"
      class="coparticipation-page"
    >
      <span
        class="primary--text font-weight-bold"
        style="font-size: 24px;"
      >
        {{ $t('coparticipation.title') }}
      </span>

      <v-tabs
        v-model="tab"
        background-color="transparent"
        color="primary"
      >
        <v-tab
          key="statement"
          href="#statement"
        >
          {{ $t('coparticipation.tabs.statement') }}
        </v-tab>
        <v-tab
          key="reports"
          href="#reports"
        >
          {{ $t('coparticipation.tabs.reports') }}
        </v-tab>
      </v-tabs>

      <v-tabs-items
        v-model="tab"
        class="transparent fill-width overflow-visible"
        touchless
      >
        <v-tab-item
          key="statement"
          value="statement"
          class="transparent"
        >
          <div class="d-flex flex-row mt-5">
            <CoparticipationFilter class="flex-grow-1" />
          </div>

          <div class="coparticipation-page__toolbar d-flex justify-end align-center my-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <Button
                  outlined
                  class="coparticipation-page__export-button"
                  v-bind="attrs"
                  v-on="on"
                  :loading="loadingExport"
                  :disabled="!canSearch || loadingTable || loadingExport"
                  @click="exportReport"
                >
                  <SvgIcon
                    src="icons/map-export.svg"
                    width="22"
                    height="22"
                    style="max-height: 22px; max-width: 22px;"
                    contain
                  />
                </Button>
              </template>
              <span>{{ $t('coparticipation.actions.export') }}</span>
            </v-tooltip>
          </div>

          <div
            v-if="showTable"
            class="mb-5"
          >
            <CoparticipationTable />
          </div>
        </v-tab-item>

        <v-tab-item
          key="reports"
          value="reports"
          class="transparent"
        >
          <div class="mt-2">
            <MyReports
              :report-type="reportType"
              :reportLayoutName="$t('coparticipation.reports.layoutName')"
              :fetch-reports="fetchCoparticipationReports"
              :hide-financial-group-filter="true"
              :tab="tab"
              is-admission-map-report
            />
          </div>
        </v-tab-item>
      </v-tabs-items>

      <ReportExportedModal
        ref="ReportExportedModal"
        @goToReport="goToReport"
      />
    </div>

    <SnackbarCustomize ref="CoparticipationSnackbar" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Authorities from '@/shared/enums/authorities';
import AuthorityManagementMixin from '@/shared/mixins/authorityManagement/authorityManagementMixin';
import CoparticipationFilter from '@/components/Coparticipation/CoparticipationFilter.vue';
import CoparticipationTable from '@/components/Coparticipation/CoparticipationTable.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import ReportExportedModal from '@/components/InvoiceParameters/modals/ReportExportedModal.vue';
import Button from '@/components/Default/Button/Button.vue';
import SvgIcon from '@/components/Default/SvgIcon.vue';
import CoparticipationService from '@/services-http/servicos/CoparticipationService';
import { COPARTICIPATION_REPORT_TYPE } from '@/store/modules/coparticipation/coparticipation-config';
import MyReports from '../myReports/MyReports.vue';

export default {
  name: 'Coparticipation',
  components: {
    CoparticipationFilter,
    CoparticipationTable,
    SnackbarCustomize,
    ReportExportedModal,
    Button,
    SvgIcon,
    MyReports,
  },
  mixins: [AuthorityManagementMixin],
  data: () => ({
    hasShownNoPermissionSnackbar: false,
    tab: 'statement',
    reportType: COPARTICIPATION_REPORT_TYPE,
  }),
  computed: {
    ...mapGetters({
      showSnackbarCustomize: 'coparticipation/showSnackbarCustomize',
      showTable: 'coparticipation/showTable',
      canSearch: 'coparticipation/canSearch',
      loadingExport: 'coparticipation/loadingExport',
      loadingTable: 'coparticipation/loadingTable',
    }),
    canShowPage() {
      return this.hasViewPermission();
    },
  },
  watch: {
    showSnackbarCustomize: {
      handler(snackbar) {
        if (snackbar && snackbar.show) {
          const { type, message, timeout } = snackbar;
          this.$refs.CoparticipationSnackbar.open(type, message, timeout);
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      initPage: 'coparticipation/initPage',
      showSnackbar: 'coparticipation/showSnackbar',
      exportRecords: 'coparticipation/exportRecords',
    }),
    hasViewPermission() {
      return this.hasPermission(Authorities.viewCoparticipation);
    },
    notifyMissingPermission() {
      if (this.hasViewPermission() || this.hasShownNoPermissionSnackbar) return;
      this.showSnackbar({
        type: 'error',
        message: String(this.$t('coparticipation.messages.noPermission')),
        show: true,
      });
      this.hasShownNoPermissionSnackbar = true;
    },
    async exportReport() {
      if (!this.canSearch) return;

      const exported = await this.exportRecords();
      if (exported) {
        setTimeout(() => {
          this.$refs.ReportExportedModal.open();
        }, 1000);
      }
    },
    fetchCoparticipationReports(params) {
      const service = new CoparticipationService();
      const reportParams = {};
      ['receivingDateStart', 'receivingDateEnd', 'status', 'reportLayout', 'page', 'size', 'sort'].forEach((key) => {
        const value = params.get(key);
        if (value) reportParams[key] = value;
      });
      return service.fetchReports(reportParams);
    },
    goToReport() {
      this.tab = 'reports';
    },
  },
  async created() {
    if (!this.hasViewPermission()) return;
    await this.initPage();
  },
  mounted() {
    this.$nextTick(() => {
      this.notifyMissingPermission();
    });
  },
};
</script>

<style lang="scss">
.coparticipation-page {
  margin: 20px;
  min-height: calc(100vh - 280px);

  &__export-button {
    min-width: 44px !important;
    width: 44px !important;
    height: 44px !important;
    padding: 0 !important;
  }
}
</style>
